import { z } from 'zod';

import { TimeZoneSchema } from '../../geo/time_zone.js';
import { ChatChannelSchema } from '../card/card.js';
import { LocaleSchema } from '../traveler/travel_preferences.js';
import { PolicyAccessSchema } from '../policy/policy_access.js';
import {
  NotificationCountsSchema,
  NotificationInboxItemSchema,
} from '../notification_inbox/notification_inbox.js';
import { DeskAgentProfileSchema, DeskSessionMarkerSchema } from '../desk/desk_session.js';
import {
  AttachmentSchema,
  ConversationIdSchema,
  MessageDirectionSchema,
  MessageIdSchema,
} from './message_envelope.js';
import { MAX_TURN_STEPS, TurnStepSchema, TurnSummaryIdSchema } from './agents.js';
import { MessageFeedbackSchema } from './message_feedback.js';
import { MessageAuthorSchema } from './message_author.js';
import { MentionListSchema } from './mention.js';
import { ConversationStateSchema } from './state.js';
import { GroupEventsEntrySchema } from '../../group_events/group_events_entry.js';
import { HomeIslandEntrySchema } from '../../home_island/home_island_entry.js';
import { KmRefundEntrySchema } from '../../km_refund/km_refund_entry.js';
import { KmTrackedEntrySchema } from '../../km_refund/km_tracked_entry.js';
import { Mobility99EntrySchema } from '../../mobility/mobility99_entry.js';
import { MobilityScheduleEntrySchema } from '../../mobility/mobility_scheduling_entry.js';
import { RideBatchesEntrySchema } from '../../mobility/ride_batch_entry.js';
import { RequestedFollowEntrySchema } from '../traveler/requested_follow.js';
import { RequestedEntrySchema } from '../traveler/user_profile.js';
import { VollWalletEntrySchema } from '../../voll_wallet/voll_wallet_entry.js';

/**
 * Generative-UI payloads (`ui`, `confirmation.selections`, `resolutions`) ride
 * OPAQUE on the domain wire (`z.unknown()`): their canonical schemas
 * (`ChatUiSchema`, `BookingSelectionsSchema`, `UiResolutionListSchema`) live in
 * `@voll-intelligence/genui-kit` — the single owner of the generative-UI
 * contract. Validation happens at the service boundaries that consume them
 * (app-integration `parseUiBlock`, the agents/conversation confirmation
 * readers, web-chat render), so a contract drift degrades gracefully instead
 * of dragging the React kit into the pure domain package (which would invert
 * the dependency and pull React into every backend).
 */

// Structured confirmation of a sensitive action — rides OUTSIDE the free text
// (the agents service injects it into the tool-side config; the LLM never
// controls it). `selections` is opaque (validated against genui-kit schemas
// downstream). Reused by ConversationInbound so the submit survives the
// app→conversation hop.
export const AppChannelConfirmationSchema = z.object({
  challengeId: z.string().min(1).max(200),
  selections: z.unknown().optional(),
  observation: z.string().max(500).optional(),
  decision: z.enum(['approve', 'deny']).optional(),
  justification: z.string().max(500).optional(),
  // Stacked-approval BULK group selector (`approval_stack` card): the
  // deterministic group the approver tapped to approve in one action — `all`,
  // one trip, or one traveler — riding OUTSIDE the free text on the SAME
  // principle as `decision` (never read off LLM prose). The approvals service
  // treats the CONFIRMED challenge `ref` as authoritative (re-derives the
  // authorized set + the Kafka VALUE + fences server-side); this is a defensive
  // cross-check only. Bulk is approve-only — there is no bulk deny.
  scope: z
    .union([
      z.literal('all'),
      z.object({ tripId: z.string().min(1) }).strict(),
      z.object({ travelerId: z.string().min(1) }).strict(),
    ])
    .optional(),
});
export type AppChannelConfirmation = z.infer<typeof AppChannelConfirmationSchema>;

/** `confirmation.selections.kind` que são CONTEXTO de um card vivo, não um
 *  submit: o refino por texto do `hotel_planner` (M1 · 1.1c, #2477) e o
 *  conjunto de picks do `flight_multi_offers` (spec flight-multi-pick-context) e o
 *  aviso da ficha do evento (`EventSheetNoticeSchema`).
 *  Nenhum deles resolve o challenge — o cartão continua vivo. BFF e conversation
 *  consultam `isCardContextConfirmation` antes de espelhar/gravar resolução; os
 *  guards dos domínios devolvem o turno ao caminho de TEXTO. */
const CARD_CONTEXT_KINDS: ReadonlySet<string> = new Set([
  'hotel_planner_refine',
  'flight_multi_leg_picks',
  'event_sheet_notice',
]);

export function isCardContextConfirmation(
  confirmation: AppChannelConfirmation | undefined,
): boolean {
  const selections = confirmation?.selections;
  if (typeof selections !== 'object' || selections === null) return false;
  const kind = (selections as { kind?: unknown }).kind;
  return typeof kind === 'string' && CARD_CONTEXT_KINDS.has(kind);
}

// Structured offer SELECTION — the deterministic offerId a button tap chose, the
// authoritative override for the NL pick. Reused by ConversationInbound.
export const AppChannelSelectionSchema = z.object({
  offerId: z.string().min(1).max(2000),
  price: z.object({ amount: z.number(), currency: z.string().min(1).max(8) }).optional(),
  // Hotel room pick refinement. Optional for backward compatibility and for
  // non-hotel selections (rental sends offerId + price, plus `page` below).
  roomIndex: z.number().int().positive().max(99).optional(),
  // Contexto da estadia refinada localmente no planner de hotel. O preview é
  // uma leitura (não cria turno), então a escolha do quarto precisa ecoar as
  // datas/ocupação que o viajante realmente viu para o checkout não voltar ao
  // checkpoint anterior do agente.
  stay: z
    .object({
      checkin: z.iso.date(),
      checkout: z.iso.date(),
      // Ocupação em HÓSPEDES, sem distinguir idade. O campo se chamou `adults`
      // até 2026-08-07; o `preprocess` aceita o nome antigo na leitura (só
      // quando o novo está ausente), para uma seleção em voo de um cliente
      // ainda não atualizado não ser recusada. Espelha
      // `HotelRoomOccupancySchema` do genui-kit.
      rooms: z
        .array(
          z.preprocess(
            (value) =>
              typeof value === 'object' &&
              value !== null &&
              'adults' in value &&
              !('guests' in value)
                ? { guests: value.adults }
                : value,
            z.object({ guests: z.number().int().min(1).max(3) }).strict(),
          ),
        )
        .min(1)
        .max(5)
        .optional(),
      // QUEM são os hóspedes, não só quantos. `rooms` sozinho ecoava a
      // ocupação e perdia os acompanhantes escolhidos no card: o checkout
      // mostrava "2 hóspedes" no cabeçalho e um viajante só na lista, porque o
      // agente caía no requester. Mesmo shape de `HotelPlannerSelection`.
      travelers: z
        .array(z.object({ id: z.string().min(1), name: z.string() }).strict())
        .min(1)
        .max(9)
        .optional(),
      policyTravelerId: z.string().min(1).optional(),
    })
    .strict()
    .refine(({ checkin, checkout }) => checkout > checkin)
    .optional(),
  // M1 · 1.1a: o contexto da TELA que o toque leva (o mesmo
  // `HotelPlannerRefineContext` que o refino por texto manda em
  // `confirmation.selections`). OPACO aqui pelo mesmo motivo daquele: o schema
  // canônico é do genui-kit, e o agents é quem valida. Sem ele, um toque depois
  // de uma busca client-side chegava a uma FSM sem lugar, sessão nem ofertas.
  refine: z.unknown().optional(),
  // #5833: a página de "Ver todos os carros" de onde saiu a oferta (número e
  // filtros do servidor) — o agente de carro a relê para achar uma oferta que
  // ele não buscou. OPACO como `refine`: o schema é do genui-contract
  // (`RentalOfferPageHintSchema`), e o agents é quem valida.
  page: z.unknown().optional(),
});
export type AppChannelSelection = z.infer<typeof AppChannelSelectionSchema>;

/**
 * Domínio escolhido por TOQUE — um atalho guiado (card `guided_flows`) ou uma
 * linha de traslado da experiência continuada (`mobility`, o agendamento de
 * carro) — pino de roteamento determinístico: o supervisor do agents entra
 * direto no subgrafo do domínio em vez de classificar a prosa por LLM (mesma
 * régua do `domainHint: 'approvals'` que o conversation stampa no
 * reply-to-elevate).
 *
 * Subconjunto FECHADO dos toques que a UI oferece — não é o enum inteiro do
 * `AgentsEvent.domainHint` (que inclui domínios sem card, como
 * `flight_schedule_change`). Roteamento NÃO é autorização: o gate do supervisor
 * (canal, policyAccess e a flag que o domínio declara) continua valendo depois do
 * pino. Há domínios que só entram por hint (`events`, fora do classificador): o
 * hint os alcança onde o texto não alcançaria, e quem os protege é esse gate — a
 * flag do rollout que o manifest declara.
 */
export const AppChannelDomainHintSchema = z.enum([
  'flight',
  'hotel',
  'rental',
  'bus',
  'mobility',
  'events',
]);
export type AppChannelDomainHint = z.infer<typeof AppChannelDomainHintSchema>;

export const AppChannelInboundRequestSchema = z
  .object({
    // `min(1)` vive no refine abaixo: um REQUERY SILENCIOSO de card (ex.: troca
    // de filtro no hotel_planner) envia `text: ''` com a `confirmation`
    // estruturada — a interação é do card, não prosa; o cliente não renderiza
    // balão e o card reconciliado descreve o próprio estado. Texto vazio SEM
    // confirmation continua inválido.
    text: z.string().max(4000),
    messageId: MessageIdSchema,
    // Pino de roteamento do toque num atalho guiado (ver o schema acima). Viaja
    // FORA do texto livre pela MESMA razão de `confirmation`/`selection`: a
    // escolha é do usuário, não uma leitura que o LLM faz da prosa.
    domainHint: AppChannelDomainHintSchema.optional(),
    // Structured confirmation of a sensitive action (e.g. the "Confirmar
    // reserva" button on the voucher_cancellation ChatUi card, or the unified
    // product_checkout submit). Travels OUTSIDE the free text on purpose: the
    // agents service injects it into the tool-side config channel, so the LLM
    // never controls it — prompt injection cannot forge an approval. The
    // challenge itself is verified server-side (single-use, TTL, bound to
    // user+intent+ref).
    // `selections` is present when the confirmation comes from a
    // product_checkout submit — the picked corporate ids (motive/project/
    // justification/company/cost-center) ride alongside the challengeId.
    // Verified + mapped server-side; the LLM never sees or controls them.
    confirmation: AppChannelConfirmationSchema.optional(),
    // Structured offer SELECTION. Rides OUTSIDE the free text on the SAME
    // principle as `confirmation`: when the client taps a rental/hotel offer card,
    // the deterministic `offerId` is carried here so the agents service resolves
    // by id — never by re-interpreting the position the LLM read off the prose
    // ("opção 3"). The free text still flows for the natural-language path (e.g.
    // "quero o mais barato"); this is the authoritative override when a button
    // drove the pick. No challengeId: this is NOT a confirmation. In rental,
    // `price` is the TOTAL the customer SAW on the clicked card — the
    // authoritative baseline `select_vehicle` compares against the revalidated
    // price. Optional for backward compat: older clients may omit it.
    selection: AppChannelSelectionSchema.optional(),
    // Menções do composer (`@viajante`, `@local`, `@data`, `@viagem`) — ver
    // `MentionSchema`. Viajam FORA do texto livre pela MESMA razão de
    // `selection`: a escolha veio de um menu determinístico. O `text` continua
    // trazendo o rótulo de cada token no lugar, então um consumidor que ignore
    // este campo lê a frase inteira como antes. Ausente ⇒ turno sem menções.
    mentions: MentionListSchema.optional(),
    // Thread alvo (canal app, múltiplas conversas). O web-chat gera o id no
    // 1º POST de uma conversa nova e o repete nos envios seguintes; ausente ⇒
    // app-integration gera (garante o id). Roteado server-side por ownership.
    conversationId: ConversationIdSchema.optional(),
    // Trip a que a conversa pertence (CONV-02). O app envia quando a conversa é
    // trip-scoped; app-integration espalha p/ o envelope (top-level). Contexto de
    // conversa — NÃO confundir com confirmation.scope.tripId (escopo de aprovação).
    tripId: z.string().min(1).max(100).optional(),
    // Idioma da INTERFACE do app (#3042). O canal `app` é o ÚNICO em que a pessoa
    // ESCOLHE o idioma explicitamente, e essa escolha é o degrau mais forte do
    // `metadata.user.locale` que o agents usa na copy determinística — sem ele a
    // conta brasileira arrastava a resposta para pt-BR com o app em inglês.
    // Enum canônico (`LocaleSchema`): valor fora de pt-BR|en|es é RECUSADO, nunca
    // coagido. Ausente ⇒ cliente antigo / canal sem UI: o voto por mensagem manda.
    uiLocale: LocaleSchema.optional(),
    // Fuso do DISPOSITIVO (#3065), ao lado do idioma da interface: o canal app é
    // o único que sabe onde a pessoa está, e a recepção cumprimenta pelo horário
    // LOCAL dela em vez do fuso de operação. Só nome IANA real passa (canonizado
    // pelo runtime, ver `TimeZoneSchema`). Diferente do `uiLocale`, um valor
    // inválido NÃO recusa o corpo (`.catch`): o cliente manda o que o SO devolve
    // e não tem como saber se o ICU do servidor conhece o nome (um fuso novo ou
    // renomeado) — e uma preferência nunca pode derrubar o envio. Inválido ou
    // ausente ⇒ descartado: o servidor cai no fuso de operação (America/Sao_Paulo).
    uiTimeZone: TimeZoneSchema.optional().catch(undefined),
    // A Carteira VOLL é oferecida a quem escreve (#6278) — o `/me` do app já sabe; a
    // recepção indica a Carteira em vez de recusar o pedido de saldo. Só escolhe copy.
    // Preferência: inválido é descartado, nunca derruba o envio.
    walletOffered: z.boolean().optional().catch(undefined),
    // A corrida com a 99 é oferecida a quem escreve (#6889) — o `/me.mobility99.offer` do app;
    // o mobility leva a corrida imediata à folha da 99 em vez de encaminhar a um atendente.
    // Só escolhe a resposta (a folha e as rotas `/mobility/*` têm o próprio gate). Preferência:
    // inválido é descartado, nunca derruba o envio.
    mobility99Offered: z.boolean().optional().catch(undefined),
  })
  .strict()
  .refine((v) => v.text.length > 0 || v.confirmation !== undefined, {
    message: 'text must be non-empty unless a structured confirmation is present',
    path: ['text'],
  });

export type AppChannelInboundRequest = z.infer<typeof AppChannelInboundRequestSchema>;

// Registro do RACIOCÍNIO do turno, preso à mensagem que o fechou. O web-chat
// mostra a linha de trabalho enquanto a resposta não chega e, quando ela chega,
// a MESMA linha vira o resumo ("comparei as tarifas · 4s") e FICA acima da
// resposta, aberta num clique. Prova sem registro é promessa: por isso o
// registro é dado da mensagem, e não estado de sessão que morre no reload.
//
// Os dois campos vêm do `AgentsReplyTrace` — são medição do agente, não do
// roteiro que o cliente narra. A duração é a do turno REAL; cronometrar a
// animação daria o mesmo número em toda busca, medido por relógio mas fabricado
// do mesmo jeito.
export const AppChatReasoningSchema = z
  .object({
    // Domínio que fechou o turno (`trace.domain`: 'flight', 'hotel', …). Escolhe
    // o roteiro que o cliente narra; `null` quando o supervisor não travou um
    // domínio (turno de texto livre → roteiro genérico).
    domain: z.string().min(1).max(100).nullable(),
    // Duração medida do turno (`trace.durationMs`).
    durationMs: z.number().int().nonnegative(),
    // Os passos REAIS do turno (`trace.steps`) — ids determinísticos que o
    // web-chat traduz por locale. Opcionais e aditivos no MESMO pacote do
    // `.strict()`: mensagem antiga (e domínio sem passos) segue sem eles, e o
    // cliente vê o roteiro narrado de sempre. Texto do modelo, do Google ou do
    // fornecedor NUNCA viaja aqui — ver `TurnStepSchema`.
    steps: z.array(TurnStepSchema).max(MAX_TURN_STEPS).optional(),
    // O id do RESUMO do turno (`trace.summary`) — a linha que fica acima da
    // resposta quando ela chega.
    summary: TurnSummaryIdSchema.optional(),
  })
  .strict();
export type AppChatReasoning = z.infer<typeof AppChatReasoningSchema>;

// Minimal app-channel frame (do NOT reuse MessageResponse — the outbound topic
// doesn't carry all its fields). Contract between app-integration and web-chat.
export const AppChatMessageSchema = z
  .object({
    messageId: z.string().min(1),
    author: MessageAuthorSchema,
    text: z.string(),
    at: z.iso.datetime(),
    // Generative-UI block rendered by web-chat below the text bubble.
    // Agent-authored frames only; absent on plain-text replies. Opaque on the
    // wire — validated against genui-kit `ChatUiSchema` at the consuming border.
    ui: z.unknown().optional(),
    // Canal de origem da mensagem. OPTIONAL: nem todo produtor o popula (a
    // mensagem otimista do web-chat e fixtures não têm), mas history route e
    // SSE handler sempre populam. Ausente ⇒ consumidor trata como canal atual
    // ('app') — usado pelo web-chat pra agrupar mensagens de troca de canal.
    channel: ChatChannelSchema.optional(),
    // Conversa à qual a mensagem pertence. OPTIONAL p/ backward-compat (a
    // mensagem otimista do web-chat não tem). history route e SSE handler
    // populam, e o web-chat filtra o push pela thread ativa.
    conversationId: ConversationIdSchema.optional(),
    // Registro do raciocínio do turno (ver AppChatReasoningSchema). Só o turno
    // do agente tem; ausente em mensagem de cliente/operador/sistema, na
    // otimista do web-chat e em todo histórico anterior a este campo.
    reasoning: AppChatReasoningSchema.optional(),
    // Voto do viajante nesta resposta (só turno do agente). Vem do history
    // (sessão do conversation); ausente na otimista e no push SSE.
    feedback: MessageFeedbackSchema.optional(),
    // Menções que a pessoa enviou com este turno (#4011) — as MESMAS do
    // `mentions` do request, para o web-chat redesenhar cada `@` como token na
    // bolha. Só mensagem de cliente: a resposta do agente herda o
    // `channelMetadata` do inbound, e não mencionou nada. Ausente na resposta do
    // agente, no push SSE e em turno sem menções.
    mentions: MentionListSchema.optional(),
    // O id que o CLIENTE deu a esta mensagem do viajante (#5514) — o
    // `externalMessageId` com que o conversation deduplica um reenvio. O
    // `messageId` do histórico é o do servidor (feedback e as outras rotas usam
    // esse), então o reenvio de uma mensagem recarregada leva ESTE id, ou vira um
    // segundo turno. Só no histórico e só em mensagem do cliente; ausente na
    // otimista (lá o `messageId` já é o do cliente) e no push SSE.
    clientMessageId: z.string().min(1).max(200).optional(),
    // Arquivos enviados com a mensagem (hoje: anexo de um atendente Zendesk).
    // O `text` traz a legenda ou um aviso. Ausente quando não há nenhum. O
    // push SSE é parseado ESTRITO pelo web-chat: o bundle que conhece este
    // campo precisa estar no ar antes de um app-integration que o envie.
    attachments: z.array(AttachmentSchema).max(20).optional(),
    // Zendesk desk: who answered (operator turns) and the divider where a human
    // session starts or ends. Both absent on every other frame. Like
    // `attachments`, the SSE push is parsed STRICT by web-chat: the bundle that
    // knows these fields must be live before an app-integration that sends them.
    deskAgent: DeskAgentProfileSchema.optional(),
    deskSession: DeskSessionMarkerSchema.optional(),
    // O aviso do inbox que esta mensagem de sistema carrega (#6277): a conversa
    // em que um aviso abre começa pelo card dele, e o aviso novo do mesmo voo
    // entra nela do mesmo jeito. Só `author: 'system'`; ausente em todo o resto.
    // Como `attachments`, o push SSE é parseado ESTRITO pelo web-chat — só uma
    // conversa aberta por um aviso o carrega, e só o bundle que a abre a cria.
    notice: NotificationInboxItemSchema.optional(),
  })
  .strict();
export type AppChatMessage = z.infer<typeof AppChatMessageSchema>;

export const AppChatPushSchema = z
  .object({ v: z.literal(1), messages: z.array(AppChatMessageSchema) })
  .strict();
export type AppChatPush = z.infer<typeof AppChatPushSchema>;

// Live per-card resolution push (Phase 2). Broadcast on the SAME /stream socket
// as message frames, keyed by the user's Cognito sub. Lets web-chat freeze an
// interactive card (product_checkout / voucher_cancellation) the instant
// another tab — or the same tab — taps confirm, without waiting for a
// /history reload.
// The reload projection (resolvedInteractionIds) stays the source of truth on
// refresh; this frame is the live mirror. Distinct shape from AppChatPush
// (`resolutions` vs `messages`) so the consumer disambiguates by structure.
export const AppChatResolutionPushSchema = z
  // `resolutions` opaque on the wire — validated against genui-kit
  // `UiResolutionListSchema` by the producer (app-integration) and web-chat.
  .object({ v: z.literal(1), resolutions: z.array(z.unknown()) })
  .strict();
export type AppChatResolutionPush = z.infer<typeof AppChatResolutionPushSchema>;

// Live inbox-notification push (notifications-inbox milestone). Broadcast on
// the SAME /stream socket as message frames, keyed by the user's Cognito sub —
// same discrimination-by-structure precedent as AppChatResolutionPushSchema: a
// DISTINCT shape (`notifications` + `counts` vs `messages` vs `resolutions`)
// lets the web-chat consumer disambiguate frames with no envelope
// discriminator. `counts` rides along so the bell badge updates without a
// counts round-trip; the pull endpoints stay the source of truth on reload.
export const AppChatNotificationsPushSchema = z
  .object({
    v: z.literal(1),
    notifications: z.array(NotificationInboxItemSchema),
    counts: NotificationCountsSchema,
  })
  .strict();
export type AppChatNotificationsPush = z.infer<typeof AppChatNotificationsPushSchema>;

// ── Conversation history list (Phase 2, 2026-06) ──────────────────────────────
// Contract for the web-chat history drawer: the user's past app conversations,
// most-recent first. `title` is owned by the conversation service: a persisted
// semantic title when generation has enough context, otherwise a useful preview
// of the first customer message; null when neither exists, in which case the web
// renders a formatted-date fallback. The conversation service is the source of
// the list (scoped by the user's `sub`).
export const AppConversationSummarySchema = z
  .object({
    conversationId: ConversationIdSchema,
    /** Semantic title or useful first-message preview; null when neither exists. */
    title: z.string().nullable(),
    state: ConversationStateSchema,
    lastMessageAt: z.iso.datetime(),
    messageCount: z.number().int().nonnegative(),
    // Optional because home/generic conversations remain unscoped and older
    // conversation-service builds did not project this field. Feeds the
    // `?tripId=` per-trip filter; per-trip display data comes from trip-api on
    // the client, not from this wire.
    tripId: z.string().min(1).max(100).optional(),
    // Board do solicitante (spec 2026-09-12 §3.1): projetados pelo conversation
    // SÓ quando a lista foi pedida com `handledBy=human`. A coluna "Conversas"
    // também projeta `handledBy`, só quando `'human'` (aba Agentes, 2026-10-23).
    handledBy: z.enum(['ai', 'human']).optional(),
    lastMessageDirection: MessageDirectionSchema.optional(),
    /** Preview da ÚLTIMA mensagem (mesmo corte de `previewTitle`); null sem texto. Toda
     *  leitura da lista e a coluna a projetam (#4881); opcional para builds antigos. */
    lastMessagePreview: z.string().nullable().optional(),
    // Conversa arquivada pelo viajante (ou pela viagem dele, em cascata). O
    // conversation SÓ a projeta quando verdadeira, e a lista por recência já a
    // omite por padrão — ela só chega aqui com `?archived=include` (a busca e o
    // "Mostrar arquivadas" do drawer) ou nas leituras por viagem/board, que
    // devolvem tudo e deixam a tela decidir. Mesmo molde do `archived` do TripView.
    archived: z.boolean().optional(),
    // Coluna "Conversas" (2026-09-28): `pendingSince` e `lastMessageAuthor` são
    // SÓ da leitura `GET /conversations/column`; `unread` vem também na lista
    // (#4881). `unread` só quando verdadeiro (produtor omite o falso);
    // `pendingSince` = pendência aberta mais antiga da conversa;
    // `lastMessageAuthor` rotula a prévia ("Atendimento VOLL: …").
    unread: z.literal(true).optional(),
    pendingSince: z.iso.datetime().optional(),
    lastMessageAuthor: MessageAuthorSchema.optional(),
  })
  .strict();
export type AppConversationSummary = z.infer<typeof AppConversationSummarySchema>;

/** Corpo de `POST /conversations/:id/archived` (BFF ↔ web-chat) e do espelho
 *  interno no conversation. `false` desarquiva. Reversível: nada muda no `state`. */
export const ConversationArchivedRequestSchema = z.object({ archived: z.boolean() }).strict();
export type ConversationArchivedRequest = z.infer<typeof ConversationArchivedRequestSchema>;

/**
 * Cascata viagem → conversas. Resposta do conversation ao arquivar/desarquivar as
 * conversas de UMA viagem, e o que a BFF repassa ao web-chat dentro de
 * `AppTripArchivedResponse`: `archived` são as conversas ESCONDIDAS depois da
 * chamada (as que esta viagem levou e as que já estavam arquivadas), `visible` as
 * que ficaram à vista — ao arquivar, porque outra viagem ATIVA ainda as segura;
 * ao desarquivar, todas as que voltaram. O web-chat tira os pins de `archived` e
 * avisa no toast quando `visible` sobra.
 */
export const TripConversationsArchivedResultSchema = z
  .object({ archived: z.array(ConversationIdSchema), visible: z.array(ConversationIdSchema) })
  .strict();
export type TripConversationsArchivedResult = z.infer<typeof TripConversationsArchivedResultSchema>;

/** Resposta de `POST /trips/:tripId/archived` da BFF: o estado gravado na viagem
 *  mais o desfecho da cascata. `conversations: null` = a viagem foi gravada mas a
 *  cascata FALHOU (o conversation não respondeu): a tela avisa em vez de a BFF
 *  desfazer a viagem — desarquivar e arquivar de novo refaz a cascata. */
export const AppTripArchivedResponseSchema = z
  .object({
    id: z.string(),
    archived: z.boolean(),
    conversations: TripConversationsArchivedResultSchema.nullable(),
  })
  .strict();
export type AppTripArchivedResponse = z.infer<typeof AppTripArchivedResponseSchema>;

export const AppConversationListSchema = z
  .object({
    v: z.literal(1),
    items: z.array(AppConversationSummarySchema),
    /** Opaque cursor for the next page; null when there are no more items. */
    nextCursor: z.string().nullable(),
  })
  .strict();
export type AppConversationList = z.infer<typeof AppConversationListSchema>;

/** `GET /conversations/column` (BFF ↔ web-chat): a coluna Conversas inteira —
 *  precisam de você + abertas dos últimos 7 dias. Sem cursor: o servidor já corta. */
export const AppConversationColumnSchema = z
  .object({ v: z.literal(1), items: z.array(AppConversationSummarySchema) })
  .strict();
export type AppConversationColumn = z.infer<typeof AppConversationColumnSchema>;

// ── Traveler profile (rodapé da sidebar do chat) ──────────────────────────────
// Identidade do viajante para o chip do rodapé: avatar (inicial) + nome +
// organização. Vem do `CustomerSnapshot` que o conversation persiste no
// `identity_binding` (o `companyName` do snapshot é renomeado para `organization`
// na projeção). Best-effort / fail-open: ambos `null` até o 1º inbound resolver o
// traveler (ou quando não há binding) — a UI esconde o chip nesse caso.
// `id` é o id do viajante no roster de negócio (o `userId` do chat token, NUNCA do
// cliente) — o app-integration o acrescenta na projeção do `/me`. Opcional porque
// a fonte upstream (o snapshot do conversation) não o carrega: quem responde é o
// BFF, a partir do token verificado. Consumidor: a busca do fluxo determinístico,
// que semeia o requester no campo QUEM (no chat esse seed vem do agents).
// `products` são os produtos que o viajante pode reservar (`PolicyAccess`, o mesmo
// veredito que o BFF aplica nas rotas determinísticas e o supervisor do agente no
// roteamento). Também acrescentado só pelo BFF, do lookup do customer-api; a UI
// esconde os atalhos de auto-serviço dos produtos ausentes DA LISTA. Opcional
// porque a fonte upstream não o carrega E porque o BFF só o inclui quando SABE:
// `[]` afirma "nenhum produto"; a chave ausente diz "não se sabe" (policy não
// resolvida, lane fora) e a UI MOSTRA os atalhos — o bloqueio real é o 403
// `product_not_enabled` do BFF (#3306; `web-chat/ui/booking/self_service_products.ts`).
export const AppProfileSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().nullable(),
    organization: z.string().nullable(),
    products: PolicyAccessSchema.optional(),
    // Foto do perfil (#2640): URL pré-assinada de vida curta, costurada só pelo BFF
    // (lote de fotos do customer-api, o próprio viajante como viewer). `null` = sem
    // foto; chave ausente = BFF antigo ou lookup fora — a UI cai nas iniciais nos dois.
    photoUrl: z.url().nullable().optional(),
    // Corrida com a 99 (spec 2026-09-29): o veredito da regra `mobility99Entry`,
    // costurado só pelo BFF. Chave AUSENTE = BFF antigo, lane desligada ou NENHUM dado
    // resolvido: a UI ESCONDE a pílula (fail-closed, ao contrário de `products`).
    // `{ offer: false, unavailable: true }` (#7318): uma leitura falhou e o resto aponta para a
    // 99 — a Home mantém a Mobilidade com o aviso e relê, como a `vollWallet`. Só sai para quem pede
    // `/me?mobilityState=1`: este schema é estrito, e um bundle anterior recusaria o `/me` inteiro.
    mobility99: Mobility99EntrySchema.optional(),
    // Agendamento de corrida (#7323): o veredito da regra `mobilitySchedulingVerdict`, costurado
    // só pelo BFF e SÓ quando NÃO oferece — `{ offer: false }` esconde a porta "Agendamento",
    // `{ offer: false, unavailable: true }` a mantém e a tela avisa que não deu para verificar.
    // Chave AUSENTE = oferecido, BFF antigo ou nada se sabe: a porta segue a política `mobility`.
    // Só sai para quem pede `/me?mobilityState=1`, pelo mesmo motivo do `mobility99.unavailable`.
    mobilitySchedule: MobilityScheduleEntrySchema.optional(),
    // Wallet de despesas: o veredito da regra `vollWalletVerdict`, costurado só pelo BFF e SÓ
    // quando oferece ou quando está INDISPONÍVEL (`{ offer: false, unavailable: true }`: a
    // leitura de flags/política falhou para quem a carteira é provável — a tela avisa e relê,
    // #6450). Chave AUSENTE = escondida (fail-closed); nunca sai um `offer: false` puro.
    vollWallet: VollWalletEntrySchema.optional(),
    // Telas de evento do organizador: o veredito da regra `groupEventsEntry`, costurado só pelo
    // BFF e SÓ quando oferece. Chave AUSENTE = escondida (fail-closed); nunca sai `offer: false`,
    // para um bundle anterior a este campo não receber a chave antes de a flag ligar.
    groupEvents: GroupEventsEntrySchema.optional(),
    // shortcut: a ilha saiu da flag e ninguém lê esta chave; ela fica enquanto um BFF anterior
    // ainda a manda (o parse é estrito). Remover com o `HomeIslandEntrySchema`.
    homeIsland: HomeIslandEntrySchema.optional(),
    // Reembolso de KM: o veredito da regra `kmRefundEntry`, costurado só pelo BFF e SÓ quando
    // oferece. Chave AUSENTE = escondido (fail-closed); nunca sai `offer: false`, como o `vollWallet`.
    kmRefund: KmRefundEntrySchema.optional(),
    // Trajeto rastreado do KM (check-in e check-out): o veredito de `kmTrackedEntry`, separado do
    // `kmRefund` (que é o manual) e SÓ quando oferece, pelo mesmo motivo do `vollWallet`.
    kmTracked: KmTrackedEntrySchema.optional(),
    // Corridas 99 em lote: o gate do gestor e o nome do cliente, costurados só pelo BFF e SÓ
    // quando libera. Chave AUSENTE = escondida; nunca sai `allowed: false`, como o `groupEvents`.
    rideBatches: RideBatchesEntrySchema.optional(),
    // Acompanhar os executivos que o solicitante atende: o gate do perfil e da flag, costurado só
    // pelo BFF e SÓ quando libera. Chave AUSENTE = escondido; nunca sai `allowed: false`, como o
    // `rideBatches`.
    requestedFollow: RequestedFollowEntrySchema.optional(),
    // A tela Solicitações (solicitante e admins): o veredito do perfil, costurado só pelo BFF.
    // Aqui sai também `allowed: false` — a UI esconde o menu só com ele; a chave
    // AUSENTE deixa o menu como sempre e o 403 do `/requested-trips` segue sendo a cerca.
    requested: RequestedEntrySchema.optional(),
  })
  .strict();
export type AppProfile = z.infer<typeof AppProfileSchema>;
